/**
 * The far horizon: ridgelines, hills, anything that hides the sun before it
 * reaches the array.
 *
 * The engine already models this - pv_core/horizon.py zeroes the beam while the
 * sun is behind terrain, from a profile fetched from PVGIS - but the viewer drew
 * none of it. So a site with a ridge to the south-east showed a sun rising into
 * open sky while the simulation had already cut the beam: our own picture
 * disagreeing with our own model, and the picture was the optimistic one.
 *
 * The interpolation here mirrors pv_core.horizon.horizon_elevation exactly,
 * including its wrap through north. Change one and change the other.
 */

/**
 * Coerce a stored profile into sorted [azimuth, elevation] pairs.
 *
 * Accepts what the backend stores - an object keyed by azimuth - and a plain
 * list of pairs. Anything unusable becomes null rather than a guess: a wrong
 * skyline is worse than none, because it looks authoritative.
 */
export function normalizeHorizon(profile) {
  if (!profile) return null;
  const entries = Array.isArray(profile)
    ? profile.map((p) => (Array.isArray(p) ? [Number(p[0]), Number(p[1])] : null))
    : Object.entries(profile).map(([az, el]) => [Number(az), Number(el)]);
  const points = entries
    .filter((p) => p && Number.isFinite(p[0]) && Number.isFinite(p[1]))
    .map(([az, el]) => [((az % 360) + 360) % 360, el])
    .sort((a, b) => a[0] - b[0]);
  if (points.length < 2) return null;
  // A profile that is flat everywhere is not far shading, it is a placeholder.
  if (points.every(([, el]) => el <= 0.01)) return null;
  return points;
}

/**
 * Terrain elevation angle at a given solar azimuth, by circular interpolation.
 *
 * Circular because the profile has no ends: the sun crosses north, and a
 * clamped lookup there would step rather than slide.
 */
export function horizonElevationAt(points, azimuthDeg) {
  if (!points || points.length === 0) return 0;
  const az = ((Number(azimuthDeg) % 360) + 360) % 360;
  const n = points.length;
  let i = 0;
  while (i < n && points[i][0] <= az) i += 1;
  const [a0, e0] = points[(i - 1 + n) % n];
  const [a1, e1] = points[i % n];
  let span = a1 - a0;
  if (span <= 0) span += 360;                    // the wrap through north
  let along = az - a0;
  if (along < 0) along += 360;
  const f = span > 0 ? along / span : 0;
  return e0 + (e1 - e0) * f;
}

/** Is the beam blocked by terrain at this sun position? */
export function beamBlockedByHorizon(points, azimuthDeg, elevationDeg) {
  if (!points) return false;
  return elevationDeg < horizonElevationAt(points, azimuthDeg);
}

/** A one-line description, for a chip or a log. */
export function describeHorizon(points) {
  if (!points) return "no terrain profile";
  const max = points.reduce((m, [, el]) => Math.max(m, el), 0);
  const worst = points.find(([, el]) => el === max);
  return `${points.length} points, highest ${max.toFixed(1)}° at ${worst[0].toFixed(0)}°`;
}
